// Owns the max-diff block state: which measure the items are read in, the chart's mode and zoom,
// and the sort order. Seeded
// from the question's params and re-seeded whenever any of them changes outside the block.
//
// Unlike the answer-variants block this is shaped after, a max-diff block has no subtree to answer
// for: its items are batches, and none of them opens into a question of its own. So the exporter
// bridges speak for this block alone.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getModelValue, getTrait } from '@aytm/app/stats/lib/stat-page'
import { isTraitType as calcIsTraitType } from '@aytm/app/stats/page/components/lib/utils'
import { getDefaultChartMode } from '@aytm/app/stats/page/question-views/md/components/chart/lib/chartModes'
import { getDefaultViewMode, VIEW_MODES } from '@aytm/app/stats/page/question-views/md/lib/controls-utils'
import { calcDisplayRange } from '@aytm/app/stats/page/question-views/md/lib/display-range'
import {
  calcChartParamsSection,
  calcSortParamsSection,
  calcViewModeReset,
  calcViewModeSection,
  calcZoomSection,
  resolveReportParams
} from '@aytm/app/stats/page/question-views/md/lib/report-params'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import useParamsReseed, { isUnchanged } from '@aytm/app/stats/page/question-views/components/hooks/useParamsReseed'

export default function useMaxDiffState({
  params,
  paramsRevision,
  questionKey,
  type,
  format,
  modelChartMode = null,
  answer,
  batches,
  itemsPerSet,
  defaultSortOrder,
  onReady,
  onStateChange
}) {
  const context = { key: questionKey, type, format, modelChartMode, answer, batches, itemsPerSet }

  const contextRef = useRef()
  contextRef.current = { ...context, defaultSortOrder }

  const [state, setState] = useState(() => resolveReportParams({ ...contextRef.current, params: params || {} }))

  const snapshot = useRef()
  snapshot.current = { state, context }

  // the mounted chart knows the axis it is showing; the exporters ask the state for it
  const chartApiRef = useRef(null)
  const onChartReady = useCallback(api => {
    chartApiRef.current = api
  }, [])

  const pendingReason = useRef('seed')

  const onModelChange = useCallback(
    (change = {}) =>
      setState(current => {
        if (Object.entries(change).every(([key, value]) => current[key] === value)) return current

        pendingReason.current = 'state'

        return { ...current, ...change }
      }),
    []
  )

  const api = useMemo(() => {
    // the mode the chart opens on once the question and global defaults are applied -- what the
    // exporters draw by, as opposed to `chartMode`, which is null until the researcher picks one
    const resolveExportChartMode = () => {
      const { state, context } = snapshot.current

      return getDefaultChartMode({
        chartMode: state.chartMode,
        modelChartMode: context.modelChartMode,
        globalChartMode: getModelValue('chartMode'),
        batches: context.batches
      })
    }

    // the axis the chart is showing. The rendered chart knows it, zoom included, but only for the
    // measure it is drawn in -- asked about another one, the maths answers instead.
    const resolveDisplayRange = (chartMode, viewMode, zoomLevel) => {
      const { state, context } = snapshot.current
      if (getTrait() && calcIsTraitType(context)) return null

      if (viewMode === state.viewMode) {
        const extremes = chartApiRef.current?.getAxisExtremes?.()
        if (null != extremes) return extremes
      }

      return calcDisplayRange({
        chartMode,
        viewMode: viewMode || state.viewMode,
        zoomLevel: zoomLevel || state.zoomLevel || getModelValue('zoomLevel'),
        globalChartMode: getModelValue('chartMode'),
        answer: context.answer,
        batches: context.batches,
        confidenceLevels: PageGlobals.CONFIDENCE_LEVELS
      })
    }

    return {
      getState: () => ({ ...snapshot.current.state }),
      getReportParamsSection: () => {
        const { state, context } = snapshot.current

        return {
          ...calcViewModeSection(state.viewMode, getDefaultViewMode(context.format, context.answer)),
          ...calcChartParamsSection({ chartMode: state.chartMode, format: context.format }),
          ...calcZoomSection(state.zoomLevel, getModelValue('zoomLevel')),
          ...calcSortParamsSection(state.sortOrder, contextRef.current.defaultSortOrder)
        }
      },
      // pptx / summary bridge: what the block is showing. The deck draws every measure, not just
      // the one on screen, so the axis is answered for each of them.
      getExportData: () => {
        const { state } = snapshot.current
        const chartModeResolved = resolveExportChartMode()

        return {
          viewMode: state.viewMode,
          chartMode: state.chartMode,
          chartModeResolved,
          displayRanges: VIEW_MODES.reduce(
            (acc, viewMode) =>
              Object.assign(acc, { [viewMode]: resolveDisplayRange(chartModeResolved, viewMode, null) }),
            {}
          ),
          sortOrder: state.sortOrder
        }
      },
      getDisplayRange: resolveDisplayRange,
      update: onModelChange
    }
  }, [onModelChange])

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  // A param moved outside the block (another view of the question, the URL, an undo): re-seed.
  useParamsReseed(params ?? {}, paramsRevision, section => {
    const next = resolveReportParams({ ...contextRef.current, params: section })
    setState(current => {
      if (isUnchanged(current, next)) return current
      pendingReason.current = 'seed'
      return next
    })
  })

  // Which measure a question opens in can be answer-versioned, and the answer arrives after the
  // block is on screen -- so the default is resolved again when it lands. Only a block still
  // showing the default it opened with follows: a measure the researcher picked stays picked, and
  // a default adopted here is not a pick, so nothing is written down for it.
  const lastAnswer = useRef(answer)
  const openedOn = useRef(getDefaultViewMode(format, answer))
  useEffect(() => {
    if (lastAnswer.current === answer) return
    lastAnswer.current = answer

    const previous = openedOn.current
    const next = getDefaultViewMode(contextRef.current.format, answer)
    openedOn.current = next

    if (next === previous) return

    setState(current => (current.viewMode === previous ? { ...current, viewMode: next } : current))
  }, [answer, format])

  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  useEffect(() => {
    const reason = pendingReason.current
    pendingReason.current = null

    if (reason) onStateChangeRef.current?.(reason)
  }, [state])

  // Picking a measure takes the chart's own settings with it -- see calcViewModeReset.
  const onViewModeChange = useCallback(
    viewMode => onModelChange({ viewMode, ...calcViewModeReset(viewMode) }),
    [onModelChange]
  )

  const onChartModeChange = useCallback(chartMode => onModelChange({ chartMode }), [onModelChange])

  const onZoomChange = useCallback(zoomLevel => onModelChange({ zoomLevel }), [onModelChange])

  const onSortChange = useCallback(sortOrder => onModelChange({ sortOrder }), [onModelChange])

  return {
    ...state,
    // the chart mode the question declares in its data, which the chart falls back to before the
    // report's -- and whose `none` hides the chart whatever the report says
    modelChartMode,
    onChartReady,
    onChartModeChange,
    onViewModeChange,
    onZoomChange,
    onSortChange,
    onModelChange
  }
}
